import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [token, setToken] = useState("");
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const t = params.get("token") || "";
    setToken(t);
    // Strip the token from the visible URL so it doesn't get bookmarked
    // or leaked via referer.
    if (t) {
      window.history.replaceState({}, "", "/reset-password");
    }
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (pwd !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pwd });
      setDone(true);
      toast.success("Password updated — you can sign in now");
      setTimeout(() => navigate("/login"), 1800);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reset password. The link may have expired.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bone flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <Link to="/" className="block mb-12 text-center" data-testid="reset-brand-link">
          <img
            src="/gcoms-logo.png"
            alt="GCOMS"
            className="w-16 h-16 mx-auto rounded-full bg-black p-1"
          />
          <div className="font-heading text-2xl font-bold tracking-tight mt-3">GCOMS.RN</div>
        </Link>

        <div className="module-card p-10 bg-white">
          {!token ? (
            <div data-testid="reset-no-token-panel" className="flex items-start gap-3">
              <AlertTriangle size={22} className="text-destructive shrink-0 mt-0.5" />
              <div>
                <h1 className="font-heading text-xl font-semibold">Missing reset token</h1>
                <p className="text-sm text-muted-foreground mt-2">
                  This reset link looks incomplete. Please request a new one from the sign-in
                  page.
                </p>
              </div>
            </div>
          ) : done ? (
            <div data-testid="reset-success-panel" className="flex items-start gap-3">
              <CheckCircle2 size={22} className="text-sage-deep shrink-0 mt-0.5" />
              <div>
                <h1 className="font-heading text-xl font-semibold">Password updated</h1>
                <p className="text-sm text-muted-foreground mt-2">
                  Redirecting you to sign in…
                </p>
              </div>
            </div>
          ) : (
            <>
              <div className="uppercase-label">Account recovery</div>
              <h1 className="font-heading text-3xl font-semibold mt-3">Set a new password</h1>
              <p className="text-sm text-muted-foreground mt-3">
                Choose a strong password — at least 8 characters.
              </p>
              <form onSubmit={submit} className="mt-8 space-y-5">
                <div>
                  <label className="text-sm font-medium">New password</label>
                  <input
                    data-testid="reset-password-input"
                    type="password"
                    required
                    minLength={8}
                    value={pwd}
                    onChange={(e) => setPwd(e.target.value)}
                    autoFocus
                    autoComplete="new-password"
                    className="mt-2 w-full px-4 py-3 rounded-md border border-ink/15 bg-white focus:outline-none focus:ring-2 focus:ring-sage/50"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Confirm password</label>
                  <input
                    data-testid="reset-password-confirm-input"
                    type="password"
                    required
                    minLength={8}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    autoComplete="new-password"
                    className="mt-2 w-full px-4 py-3 rounded-md border border-ink/15 bg-white focus:outline-none focus:ring-2 focus:ring-sage/50"
                  />
                </div>
                <button
                  data-testid="reset-submit-button"
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 rounded-md bg-ink text-bone font-medium hover:bg-ink/90 disabled:opacity-60 transition-colors"
                >
                  {loading ? "Updating..." : "Update password"}
                </button>
              </form>
            </>
          )}

          <div className="mt-8 text-sm text-muted-foreground text-center">
            <Link
              to="/login"
              data-testid="reset-back-to-login"
              className="inline-flex items-center gap-1.5 text-ink hover:underline"
            >
              <ArrowLeft size={14} /> Back to sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
